import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const itineraryData = {
  bali: {
    name: 'BALI',
    dates: 'January 25 – February 2',
    theme: 'Reset • Creativity • Spiritual Energy • Ocean Flow',
    color: 'text-emerald',
    days: [{
      date: 'January 25',
      title: 'Arrival + Grounding',
      activities: ['Arrive in Bali and settle into villa or resort', 'Slow sunset dinner overlooking rice fields or ocean', 'Early night + intention setting for the trip', 'Journal prompt: “What version of me am I leaving behind?”']
    }, {
      date: 'January 26',
      title: 'Ubud Immersion',
      activities: ['Morning yoga + breathwork', 'Explore Ubud markets and artisan spaces', 'Visit Tirta Empul water temple', 'Sound healing or cacao ceremony in the evening']
    }, {
      date: 'January 27',
      title: 'Jungle + Creativity Day',
      activities: ['Work session / creative strategy morning', 'Visit Tegallalang rice terraces', 'Café hopping + founder reflection time', 'Sunset dinner with live music']
    }, {
      date: 'January 28',
      title: 'Adventure + Movement',
      activities: ['ATV or waterfall tour', 'Floating breakfast or spa recovery session', 'Beach club evening in Canggu', 'Networking dinner with entrepreneurs/creatives']
    }, {
      date: 'January 29',
      title: 'Deep Reset',
      activities: ['Full wellness day', 'Massage + infrared sauna + ice bath', 'Reading / journaling block', 'Sunset meditation']
    }, {
      date: 'January 30',
      title: 'Island Energy',
      activities: ['Day trip to Nusa Penida or Nusa Lembongan', 'Snorkeling + boat experience', 'Ocean photography/content capture', 'Seafood dinner by the water']
    }, {
      date: 'January 31',
      title: 'Founder Flow Day',
      activities: ['Strategic planning session for next chapter', 'Creative brainstorming and vision mapping', 'Luxury dining experience', 'Rooftop cocktails or live DJ set']
    }, {
      date: 'February 1',
      title: 'Celebration Day',
      activities: ['Sunrise beach walk', 'Relaxed shopping and cafés', 'Final Bali dinner experience', 'Reflection prompt: “What energy am I bringing into the future?”']
    }, {
      date: 'February 2',
      title: 'Departure + Travel Reset',
      activities: ['Slow morning', 'Coffee + journaling', 'Travel prep and airport transfer']
    }]
  },
  brazil: {
    name: 'BRAZIL',
    dates: 'February 4 – February 10',
    theme: 'Expansion • Rhythm • Celebration • Creative Power',
    color: 'text-fuchsia',
    days: [{
      date: 'February 4',
      title: 'Arrival in Rio',
      activities: ['Check into luxury hotel in Rio de Janeiro', 'Sunset rooftop cocktails overlooking Copacabana', 'Late-night dinner and first taste of Carnival energy', 'Ease into the rhythm of the city']
    }, {
      date: 'February 5',
      title: 'Rio Icons + Samba Energy',
      activities: ['Morning visit to Christ the Redeemer', 'Sugarloaf Mountain views and photography', 'Relaxed beach afternoon at Ipanema', 'Evening samba rehearsal experience', 'Midnight conversations, music, and movement']
    }, {
      date: 'February 6',
      title: 'Carnival Immersion',
      activities: ['VIP Carnival bloco experience', 'Street fashion, dancing, and cultural immersion', 'Café reset and creative reflection break', 'High-energy rooftop party or curated nightlife experience']
    }, {
      date: 'February 7',
      title: 'Founder Energy + Luxury',
      activities: ['Slow luxury breakfast and beach recovery', 'Creative strategy and vision session', 'Designer shopping or art gallery exploration', 'Private dinner with founders, creatives, and operators']
    }, {
      date: 'February 8',
      title: 'Celebration + Expansion',
      activities: ['Boat day or helicopter tour over Rio', 'Content capture and storytelling moments', 'Carnival parade experience or live music immersion', 'Late-night celebration in the heart of the city']
    }, {
      date: 'February 9',
      title: 'Integration Day',
      activities: ['Wellness recovery morning', 'Spa, journaling, and reflection', 'Explore hidden local cafés and neighborhoods', 'Final Brazil dinner experience with live music', 'Reflection prompt: “What does expansion feel like in my body?”']
    }, {
      date: 'February 10',
      title: 'Departure + Transition',
      activities: ['Slow oceanfront breakfast', 'Final beach walk', 'Airport transfer and reset for Botswana']
    }]
  },
  botswana: {
    name: 'BOTSWANA',
    dates: 'February 11 – February 15',
    theme: 'Nature • Legacy • Silence • Perspective',
    color: 'text-sunset',
    days: [{
      date: 'February 11',
      title: 'Arrival + Safari Reset',
      activities: ['Arrive at luxury safari lodge', 'Sunset game drive', 'Fireside dinner under the stars', 'Disconnect from digital noise']
    }, {
      date: 'February 12',
      title: 'Wildlife + Reflection',
      activities: ['Sunrise safari drive', 'Journaling and stillness session', 'Lunch overlooking nature reserve', 'Evening storytelling around fire']
    }, {
      date: 'February 13',
      title: 'Deep Presence Day',
      activities: ['Mokoro canoe safari or walking safari', 'Photography and observation', 'Afternoon rest and reading', 'Sunset gratitude reflection']
    }, {
      date: 'February 14',
      title: 'Legacy & Vision',
      activities: ['Quiet morning meditation', 'Personal vision mapping session', 'Final safari experience', 'Luxury farewell dinner']
    }, {
      date: 'February 15',
      title: 'Departure',
      activities: ['Sunrise coffee in nature', 'Final reflections', 'Airport transfer']
    }]
  }
};
type ItineraryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  destinationId: 'bali' | 'brazil' | 'botswana' | null;
};
export function ItineraryModal({
  isOpen,
  onClose,
  destinationId
}: ItineraryModalProps) {
  if (!destinationId) return null;
  const data = itineraryData[destinationId];
  return <AnimatePresence data-id="element-210">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-211">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-rich-black/80 backdrop-blur-md" data-id="element-212" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-rich-black border border-white/10 rounded-3xl p-8 sm:p-12 shadow-2xl custom-scrollbar" data-id="element-213">
            <button onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors" data-id="element-214">
              <X size={24} data-id="element-215" />
            </button>

            <div className="mb-12 text-center" data-id="element-216">
              <h2 className={`font-display text-5xl md:text-7xl font-bold mb-4 ${data.color}`} data-id="element-217">
                {data.name}
              </h2>
              <p className="font-sans text-xl text-white font-medium mb-2" data-id="element-218">
                {data.dates}
              </p>
              <p className="font-sans text-sm tracking-widest text-gold uppercase" data-id="element-219">
                {data.theme}
              </p>
            </div>

            <div className="space-y-12 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/10 before:to-transparent" data-id="element-220">
              {data.days.map((day, idx) => <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-221">
                  {/* Timeline Node */}
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 border-rich-black bg-white/10 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2`} data-id="element-222">
                    <div className={`w-3 h-3 rounded-full ${data.color.replace('text-', 'bg-')}`} data-id="element-223" />
                  </div>

                  {/* Card */}
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors" data-id="element-224">
                    <div className="flex flex-col mb-4" data-id="element-225">
                      <span className="font-sans text-sm font-bold tracking-wider text-gold uppercase mb-1" data-id="element-226">
                        {day.date}
                      </span>
                      <h4 className="font-display text-2xl text-white" data-id="element-227">
                        {day.title}
                      </h4>
                    </div>
                    <ul className="space-y-3" data-id="element-228">
                      {day.activities.map((activity, aIdx) => <li key={aIdx} className="font-sans text-gray-300 text-sm leading-relaxed flex items-start gap-2" data-id="element-229">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${data.color.replace('text-', 'bg-')}`} data-id="element-230" />
                          <span data-id="element-231">{activity}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>)}
            </div>

            <div className="mt-16 text-center border-t border-white/10 pt-12" data-id="element-232">
              <p className="font-display text-2xl text-white italic" data-id="element-233">
                "Experience earned. Forever learning."
              </p>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}